import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { User, Mail, Shield, Save, Loader2, Key } from 'lucide-react';
import Layout from '../components/Layout';

const ProfilePage = () => {
  const { user, setUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    full_name: user?.name || '',
    email: user?.email || '',
  });
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });
  const [changingPassword, setChangingPassword] = useState(false);

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await authAPI.updateProfile(formData);
      setUser(prev => ({ ...prev, name: response.data.name, email: response.data.email }));
      localStorage.setItem('user', JSON.stringify({ ...user, name: response.data.name, email: response.data.email }));
      setEditMode(false);
      toast.success('Profile updated successfully');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (passwordData.new_password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setChangingPassword(true);
    
    try {
      await authAPI.changePassword({
        current_password: passwordData.current_password,
        new_password: passwordData.new_password,
      });
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
      toast.success('Password changed successfully');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin': return 'bg-red-500/20 text-red-400 border-red-500/50';
      case 'supervisor': return 'bg-blue-500/20 text-blue-400 border-blue-500/50';
      default: return 'bg-green-500/20 text-green-400 border-green-500/50';
    }
  };

  return (
    <Layout>
      <div className="max-w-2xl mx-auto space-y-6" data-testid="profile-page">
        {/* Header */}
        <div>
          <h1 className="font-['Barlow_Condensed'] text-4xl md:text-5xl font-bold uppercase tracking-tight text-white">
            My Profile
          </h1>
          <p className="text-slate-400 mt-1">Manage your account settings</p>
        </div>

        {/* Profile Card */}
        <div className="card-industrial">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 bg-slate-700 rounded-full flex items-center justify-center">
              <User className="w-8 h-8 text-slate-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">{user?.name}</h2>
              <p className="text-slate-400">{user?.email}</p>
              <span className={`inline-block mt-2 px-3 py-1 text-xs font-mono uppercase border rounded-sm ${getRoleBadgeColor(user?.role)}`}>
                {user?.role}
              </span>
            </div>
          </div>

          {/* Profile Form */}
          <form onSubmit={handleUpdateProfile} className="space-y-4">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <User className="w-4 h-4 text-amber-500" />
                <Label className="text-slate-400 uppercase text-xs tracking-wider">Full Name</Label>
              </div>
              {editMode ? (
                <Input
                  value={formData.full_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, full_name: e.target.value }))}
                  className="input-industrial"
                  data-testid="profile-name-input"
                />
              ) : (
                <p className="text-white py-2">{user?.name}</p>
              )}
            </div>

            <div>
              <div className="flex items-center gap-2 mb-2">
                <Mail className="w-4 h-4 text-amber-500" />
                <Label className="text-slate-400 uppercase text-xs tracking-wider">Email Address</Label>
              </div>
              {editMode ? (
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  className="input-industrial"
                  data-testid="profile-email-input"
                />
              ) : (
                <p className="text-white py-2">{user?.email}</p>
              )}
            </div>

            <div>
              <div className="flex items-center gap-2 mb-2">
                <Shield className="w-4 h-4 text-amber-500" />
                <Label className="text-slate-400 uppercase text-xs tracking-wider">Role</Label>
              </div>
              <p className="text-slate-400 py-2 capitalize">{user?.role} (Contact admin to change)</p>
            </div>

            <div className="flex gap-3 pt-4">
              {editMode ? (
                <>
                  <Button
                    type="button"
                    onClick={() => {
                      setEditMode(false);
                      setFormData({ full_name: user?.name || '', email: user?.email || '' });
                    }}
                    className="btn-secondary"
                    data-testid="cancel-edit-btn"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={loading}
                    className="btn-primary flex items-center gap-2"
                    data-testid="save-profile-btn"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    Save Changes
                  </Button>
                </>
              ) : (
                <Button
                  type="button"
                  onClick={() => setEditMode(true)}
                  className="btn-secondary"
                  data-testid="edit-profile-btn"
                >
                  Edit Profile
                </Button>
              )}
            </div>
          </form>
        </div>

        {/* Change Password Card */}
        <div className="card-industrial">
          <div className="flex items-center gap-2 mb-4">
            <Key className="w-5 h-5 text-amber-500" />
            <h3 className="font-['Barlow_Condensed'] text-lg font-bold uppercase tracking-tight text-white">
              Change Password
            </h3>
          </div>

          <form onSubmit={handleChangePassword} className="space-y-4">
            <div>
              <Label className="text-slate-400 uppercase text-xs tracking-wider mb-2 block">Current Password</Label>
              <Input
                type="password"
                value={passwordData.current_password}
                onChange={(e) => setPasswordData(prev => ({ ...prev, current_password: e.target.value }))}
                className="input-industrial"
                placeholder="Enter current password"
                data-testid="current-password-input"
              />
            </div>

            <div>
              <Label className="text-slate-400 uppercase text-xs tracking-wider mb-2 block">New Password</Label>
              <Input
                type="password"
                value={passwordData.new_password}
                onChange={(e) => setPasswordData(prev => ({ ...prev, new_password: e.target.value }))}
                className="input-industrial"
                placeholder="Enter new password (min 6 characters)"
                data-testid="new-password-input"
              />
            </div>

            <div>
              <Label className="text-slate-400 uppercase text-xs tracking-wider mb-2 block">Confirm New Password</Label>
              <Input
                type="password"
                value={passwordData.confirm_password}
                onChange={(e) => setPasswordData(prev => ({ ...prev, confirm_password: e.target.value }))}
                className="input-industrial"
                placeholder="Confirm new password"
                data-testid="confirm-password-input"
              />
            </div>

            <Button
              type="submit"
              disabled={changingPassword || !passwordData.current_password || !passwordData.new_password}
              className="btn-primary flex items-center gap-2"
              data-testid="change-password-btn"
            >
              {changingPassword ? <Loader2 className="w-4 h-4 animate-spin" /> : <Key className="w-4 h-4" />}
              Change Password
            </Button>
          </form>
        </div>
      </div>
    </Layout>
  );
};

export default ProfilePage;
